import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SvgIcon,
  Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { debounce } from '@mui/material/utils';
import { useGridApiRef } from '@mui/x-data-grid';
import { SendDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import StatusChip from 'components/common/StatusChip';
import SearchField from 'components/controls/SearchField';
import SendIcon from 'mdi-react/SendIcon';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useListAllBulkMessagingQuery,
  useListDocumentFormatsQuery,
} from 'store/app/entities/SharedFilesSlice';
import authCompany from 'store/selectors/authCompany';
import alertActions from 'store/app/ui/Alert/action';
import { isActiveOmmnioSignProvider } from 'utils/eSignatureProviderAccess';
import downloadBulkMessagingZip from 'utils/downloadBulkMessagingZip';
import { I18n } from 'utils/i18n';
import ms from 'ms';
import { useLocation, useNavigate } from 'react-router';
import SignTrackingRecipientsDrawer from './SignTrackingRecipientsDrawer';

const MONGO_ID_RE = /^[a-f\d]{24}$/i;

/** Same tokenization as `GridToolbarSearch` / DataGrid quick filter. */
const defaultSearchValueParser = searchText =>
  searchText.match(/(?:\S+:"[^"]*"|\S+:\S+|\S+)/g) || [];

const SignTrackingToolbarNone = () => null;

const statusFilters = [
  { key: 'all', status: null },
  { key: 'pending', status: 'pending' },
  { key: 'completed', status: 'completed' },
  { key: 'expired', status: 'expired' },
];

/** Progress bar color only — bucket/chip uses API trackingFilterKey. */
const getDeliveryStatusKey = status => {
  if (status === 'ABORTED') return 'expired';
  if (status === 'FINISHED') return 'completed';
  return 'pending';
};

/**
 * Status chip label. Primary bucket comes from API `trackingFilterKey` (same rule as filter chips).
 * Within `pending`, distinguish signature-in-progress from delivery-in-progress.
 */
const getRowStatusKey = row => {
  const bucket = row.trackingFilterKey;
  if (bucket === 'expired') return 'expired';
  if (bucket === 'completed') return 'completed';
  const total = row.total || 0;
  if (row.signProgressEligible === true && total > 0 && (row.signedCount ?? 0) < total) {
    return 'signingPending';
  }
  return 'pending';
};

const getStatusColor = statusKey => {
  if (statusKey === 'completed') {
    return 'success';
  }
  if (statusKey === 'expired') {
    return 'error';
  }
  return 'warning';
};

const formatDate = date => {
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(date));
};

const SignTracking = () => {
  const dispatch = useDispatch();
  const company = useSelector(authCompany);
  const showTemplateFilter = isActiveOmmnioSignProvider(company);
  const location = useLocation();
  const navigate = useNavigate();
  const sendDrawer = useContext(SendDrawerContext);
  const [recipientDrawer, setRecipientDrawer] = useState({ open: false, row: null });
  const [bulkDownloadBusy, setBulkDownloadBusy] = useState(false);
  const bulkDownloadLockRef = useRef(false);
  const [perPage, setPerPage] = useState(10);
  const [sortModel, setSortModel] = useState(undefined);
  const [filterModel, setFilterModel] = useState(undefined);
  const apiRef = useGridApiRef();
  const gridQuickFilterStr = filterModel?.quickFilterValues?.join(' ') ?? '';
  const [searchValue, setSearchValue] = useState(gridQuickFilterStr);
  const [page, setPage] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const formatIdFromSearch = useMemo(() => {
    const id = searchParams.get('format');
    return id && MONGO_ID_RE.test(id) ? id : '';
  }, [searchParams]);

  const statusKeyFromSearch = useMemo(() => {
    const status = searchParams.get('status');
    return statusFilters.some(filter => filter.status === status) ? status : null;
  }, [searchParams]);

  const [templateFilterId, setTemplateFilterId] = useState(formatIdFromSearch);

  useEffect(() => {
    setTemplateFilterId(formatIdFromSearch);
  }, [formatIdFromSearch]);

  useEffect(() => {
    const key = statusFilters.find(filter => filter.status === statusKeyFromSearch)?.key ?? 'all';
    setActiveFilter(key);
  }, [statusKeyFromSearch]);

  useEffect(() => {
    setSearchValue(gridQuickFilterStr);
  }, [gridQuickFilterStr]);

  const debouncedApplyQuickFilter = useMemo(
    () =>
      debounce(values => {
        apiRef.current?.setQuickFilterValues(values);
        setPage(0);
      }, 500),
    [apiRef],
  );

  useEffect(() => () => debouncedApplyQuickFilter.clear(), [debouncedApplyQuickFilter]);

  const handleSearchChange = useCallback(
    e => {
      const text = e.target.value;
      setSearchValue(text);
      debouncedApplyQuickFilter(defaultSearchValueParser(text));
    },
    [debouncedApplyQuickFilter],
  );

  const { data: { documentFormats = [] } = {} } = useListDocumentFormatsQuery(undefined, {
    skip: !showTemplateFilter,
  });
  const sortedFormats = useMemo(
    () =>
      [...documentFormats].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
      ),
    [documentFormats],
  );

  useEffect(() => {
    if (showTemplateFilter) return;
    if (!templateFilterId) return;
    setTemplateFilterId('');
    const params = new URLSearchParams(location.search);
    if (params.has('format')) {
      params.delete('format');
      const qs = params.toString();
      navigate(
        { pathname: location.pathname, search: qs ? `?${qs}` : '' },
        { replace: true, state: location.state },
      );
    }
  }, [
    showTemplateFilter,
    templateFilterId,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (!templateFilterId || sortedFormats.length === 0) return;
    const ok = sortedFormats.some(d => d._id === templateFilterId);
    if (!ok) {
      setTemplateFilterId('');
      const params = new URLSearchParams(location.search);
      if (params.has('format')) {
        params.delete('format');
        const qs = params.toString();
        navigate(
          { pathname: location.pathname, search: qs ? `?${qs}` : '' },
          { replace: true, state: location.state },
        );
      }
    }
  }, [
    templateFilterId,
    sortedFormats,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  const activeStatus = statusFilters.find(filter => filter.key === activeFilter)?.status;
  const search = filterModel?.quickFilterValues?.join(' ');
  const {
    data: { bulkMessaging = [], totalCount = 0, statusCounts = {} } = {},
    isFetching,
    isUninitialized,
  } = useListAllBulkMessagingQuery({
    search,
    status: activeStatus,
    format: templateFilterId || undefined,
    page: page + 1,
    perPage,
    sortBy: sortModel?.[0]?.field || 'createdAt',
    sort: sortModel?.[0]?.sort || 'desc',
  });

  const counts = useMemo(() => {
    const pending = statusCounts.pending || 0;
    const completed = statusCounts.completed || 0;
    const expired = statusCounts.expired || 0;
    return {
      all: pending + completed + expired,
      pending,
      completed,
      expired,
    };
  }, [statusCounts]);

  const handleFilterClick = key => {
    setActiveFilter(key);
    setPage(0);
    const params = new URLSearchParams(location.search);
    const status = statusFilters.find(filter => filter.key === key)?.status;
    if (status) params.set('status', status);
    else params.delete('status');
    const qs = params.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : '' },
      { replace: true, state: location.state },
    );
  };

  const handleTemplateFilterChange = useCallback(
    e => {
      const v = e.target.value;
      setTemplateFilterId(v);
      setPage(0);
      const params = new URLSearchParams(location.search);
      if (v) params.set('format', v);
      else params.delete('format');
      const qs = params.toString();
      navigate(
        { pathname: location.pathname, search: qs ? `?${qs}` : '' },
        { replace: true, state: location.state },
      );
    },
    [location.pathname, location.search, location.state, navigate],
  );

  const gridExtraUrlParams = useMemo(
    () => ({
      ...(activeStatus ? { status: activeStatus } : {}),
      ...(templateFilterId ? { format: templateFilterId } : {}),
    }),
    [activeStatus, templateFilterId],
  );

  const handleBulkDownload = useCallback(
    async (e, formatId, bulkMessagingId) => {
      e.preventDefault();
      e.stopPropagation();
      if (bulkDownloadLockRef.current) return;
      bulkDownloadLockRef.current = true;
      setBulkDownloadBusy(true);
      try {
        await downloadBulkMessagingZip({ formatId, bulkMessagingId });
      } catch (err) {
        dispatch(
          alertActions.show({
            variant: 'error',
            label: I18n.t('SignTracking.downloadBulkErrorTitle'),
            message: err?.message || I18n.t('SignTracking.downloadBulkErrorFallback'),
            autoHideDuration: ms('8 seconds'),
          }),
        );
      } finally {
        bulkDownloadLockRef.current = false;
        setBulkDownloadBusy(false);
      }
    },
    [dispatch],
  );

  const columns = [
    {
      field: 'text',
      flex: 1.7,
      minWidth: 280,
      editable: false,
      sortable: false,
      headerName: I18n.t('SignTracking.Table.document'),
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'primary.ultraLight',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
            }}
          >
            <DescriptionOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
              {params.row.formatName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {I18n.t('SignTracking.Recipients', { count: params.row.total || 0 })}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'recipientsChannel',
      flex: 0.8,
      minWidth: 150,
      editable: false,
      sortable: false,
      headerName: I18n.t('SignTracking.Table.source'),
      renderCell: params => (
        <StatusChip
          size="small"
          color="primary"
          label={I18n.t(`SignTracking.Sources.${params.value || 'internal'}`)}
          cellWrap={false}
        />
      ),
    },
    {
      field: 'sent',
      flex: 1,
      minWidth: 190,
      editable: false,
      headerName: I18n.t('SignTracking.Table.progress'),
      renderCell: params => {
        const total = params.row.total || 0;
        const signMode = params.row.signProgressEligible === true;
        const done = signMode ? (params.row.signedCount ?? 0) : params.row.sent || 0;
        const progress = total > 0 ? Math.min((done / total) * 100, 100) : 0;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              width: '100%',
              minWidth: 0,
            }}
          >
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                flex: 1,
                minWidth: 0,
                height: 6,
                borderRadius: 999,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 999,
                  bgcolor:
                    getDeliveryStatusKey(params.row.status) === 'expired'
                      ? 'error.main'
                      : 'success.main',
                },
              }}
            />
            <IconButton
              size="small"
              sx={{ flexShrink: 0, p: 0.25 }}
              onClick={e => {
                e.stopPropagation();
                setRecipientDrawer({ open: true, row: params.row });
              }}
              aria-label={I18n.t('SignTracking.recipientsDrawer.open')}
            >
              <ListAltOutlinedIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ flexShrink: 0, minWidth: 48, fontWeight: 700 }}>
              {done}/{total}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'status',
      flex: 0.8,
      minWidth: 170,
      editable: false,
      sortable: false,
      headerName: I18n.t('SignTracking.Table.status'),
      renderCell: params => {
        const statusKey = getRowStatusKey(params.row);
        const color = getStatusColor(statusKey);
        return <StatusChip color={color} label={I18n.t(`SignTracking.Status.${statusKey}`)} />;
      },
    },
    {
      field: 'createdAt',
      flex: 0.7,
      minWidth: 130,
      editable: false,
      headerName: I18n.t('SignTracking.Table.sentAt'),
      renderCell: params => (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          {formatDate(params.value)}
        </Typography>
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h3">{I18n.t('DocumentsScene.Folders.tracking')}</Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            {I18n.t('SignTracking.Description')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => sendDrawer.open(null, null, { allowFormatSelection: true })}
          sx={{ whiteSpace: 'nowrap' }}
        >
          <SvgIcon sx={{ fontSize: 18, mr: 1 }}>
            <SendIcon />
          </SvgIcon>
          {I18n.t('SentDocuments.Send document')}
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          rowGap: 1.5,
          mb: 1,
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            rowGap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          {statusFilters.map(filter => (
            <Chip
              key={filter.key}
              clickable
              variant={activeFilter === filter.key ? 'filled' : 'outlined'}
              color={activeFilter === filter.key ? 'primary' : 'default'}
              label={`${I18n.t(`SignTracking.Filters.${filter.key}`)} ${counts[filter.key] || 0}`}
              onClick={() => handleFilterClick(filter.key)}
              sx={{ borderRadius: 999, fontWeight: 600 }}
            />
          ))}
          {showTemplateFilter && (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 }, maxWidth: 320 }}>
              <InputLabel id="sign-tracking-template-filter-label">
                {I18n.t('SignTracking.templateFilter.label')}
              </InputLabel>
              <Select
                size="small"
                labelId="sign-tracking-template-filter-label"
                label={I18n.t('SignTracking.templateFilter.label')}
                value={templateFilterId}
                onChange={handleTemplateFilterChange}
              >
                <MenuItem value="">
                  <em>{I18n.t('SignTracking.templateFilter.all')}</em>
                </MenuItem>
                {sortedFormats.map(f => (
                  <MenuItem key={f._id} value={f._id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box sx={{ width: { xs: '100%', sm: 280, md: 320 }, flexShrink: 0 }}>
          <SearchField
            fullWidth
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={I18n.t('SignTracking.SearchPlaceholder')}
          />
        </Box>
      </Box>

      <OmmnioDataGrid
        apiRef={apiRef}
        slots={{ toolbar: SignTrackingToolbarNone }}
        loading={isFetching || isUninitialized}
        sx={{ flex: 1 }}
        columns={columns}
        rows={bulkMessaging}
        getRowId={r => r._id}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        page={page}
        pageSize={perPage}
        sortModel={sortModel}
        filterModel={filterModel}
        onPageChange={setPage}
        onPageSizeChange={setPerPage}
        onFilterModelChange={setFilterModel}
        onSortModelChange={setSortModel}
        defaultSortModel={[{ field: 'createdAt', sort: 'desc' }]}
        extraUrlParams={gridExtraUrlParams}
        updateHistory
        disableEvenOdd
      />
      <SignTrackingRecipientsDrawer
        open={recipientDrawer.open}
        onClose={() => setRecipientDrawer({ open: false, row: null })}
        formatId={recipientDrawer.row?.format}
        bulkMessagingId={recipientDrawer.row?._id}
        templateName={recipientDrawer.row?.formatName}
        downloadBusy={bulkDownloadBusy}
        onDownloadZip={e =>
          handleBulkDownload(e, recipientDrawer.row?.format, recipientDrawer.row?._id)
        }
      />
    </Box>
  );
};

export default SignTracking;
